import React, { useEffect, useState } from 'react';
import Breadcrumb from '@/components/common/Breadcrumb';
import Layout from '@/components/Layout/Layout';
import { Eye, Star } from 'lucide-react';
import { IRating, IRatingListResponse } from '@/interfaces/rating.interface';
import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import RatingSkeleton from '@/components/UI/loaders/RatingSkeleton';
import { svgIcons } from '@/components/UI/SvgIcons';
import {
  formatDateTime,
  GetFilterUrl,
  showToastNotification,
} from '@/utility/helper';
import { Link } from 'react-router-dom';
import { RoutesLink } from '@/utility/RouteLinks';
import CommonFilter from '@/components/common/CommonFilter';
import { PageInfo } from '@/interfaces/common.interface';
import { usePermissions } from '@/hooks/usePermissions';
import { getPermissionName } from '@/context/permission';
import { useListQueryParams } from '@/hooks/useListQueryParams';

const { CustomerIcon } = svgIcons;

interface StatesProp {
  isLoading: boolean;
  ratings: IRating[];
  pageInfo: PageInfo;
  error: string;
}

const ReviewsDetails: React.FC = () => {
  const { canEdit } = usePermissions();

  const { params, setParams } = useListQueryParams({
    status: '',
    orderDir: 'DESC',
  });

  const { page, limit, status, orderDir } = params;

  const [states, setStates] = useState<StatesProp>({
    isLoading: false,
    ratings: [],
    pageInfo: {
      total: 0,
      totalPages: 1,
      limit: limit ?? 12,
      page: page ?? 1,
    },
    error: '',
  });

  const breadcrumb = [
    { label: 'Dashboard', href: RoutesLink.dashboard },
    { label: 'Review Rating' },
  ];

  const handleChangeState = (payload: Partial<StatesProp>) => {
    setStates((prev) => ({ ...prev, ...payload }));
  };

  // 🔹 Fetch ratings
  const fetchRatings = async (reset: boolean) => {
    try {
      handleChangeState({ isLoading: true });

      const query = GetFilterUrl({
        page,
        limit,
        status,
        orderDir,
      });

      const url = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.rating}?${query}`;
      const res = await callApi<IRatingListResponse>(url, { method: 'GET' });

      handleChangeState({
        ratings: reset ? res.data : [...states.ratings, ...res.data],
        pageInfo: res.pageInfo,
      });
    } catch (err: any) {
      handleChangeState({ error: err.message });
    } finally {
      handleChangeState({ isLoading: false });
    }
  };

  useEffect(() => {
    fetchRatings(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, orderDir]);

  useEffect(() => {
    if (page > 1) fetchRatings(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleStatusChange = async (
    id: string,
    status: 'approved' | 'rejected',
  ) => {
    try {
      await callApi(
        `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.rating}/action/${id}`,
        {
          method: 'PATCH',
          data: { status },
        },
      );
      showToastNotification('success', `Status updated to ${status}`);
      setParams({ page: 1 });
    } catch (err: any) {
      showToastNotification('danger', err?.message);
    }
  };

  const reviewOptions = [
    { value: '', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
  ];

  const sortOptions = [
    { value: 'DESC', label: 'Newest First' },
    { value: 'ASC', label: 'Oldest First' },
  ];

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <Breadcrumb items={breadcrumb} />
        <CommonFilter
          activeFilter={status}
          filters={reviewOptions}
          showSearch={false}
          onChange={(value) => setParams({ status: value, page: 1 })}
          filterOptions={{
            values: sortOptions,
            onChange: (value) => setParams({ orderDir: value, page: 1 }),
          }}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {states.isLoading &&
          [...Array(6)].map((_, i) => <RatingSkeleton key={i} />)}

        {states.ratings.map((item) => (
          <div key={item.id} className="bg-white rounded-3xl p-5 h-full">
            <div className="flex justify-end text-primary">
              <Link to={`${RoutesLink.reviewRatingDetails}/${item.id}`}>
                <Eye size={20} />
              </Link>
            </div>
            <div className="flex justify-between items-center gap-2 flex-wrap mb-2">
              <div className="flex items-center gap-2">
                <span className="min-w-8 min-h-8 w-8 h-8 rounded-full border border-light-20 flex items-center justify-center">
                  {CustomerIcon}
                </span>
                <div>
                  <h3 className="text-sm sm:text-base font-normal text-gray-900">
                    {item?.user?.fullName}
                  </h3>
                  <span className="text-xs sm:text-sm text-gray-300">
                    {formatDateTime(item.createdAt, 'YYMMMDDHH')}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }, (_, i) => (
                  <Star
                    key={i}
                    color="#FF9811"
                    fill={i < item?.rating ? '#FF9811' : 'transparent'}
                    size={20}
                  />
                ))}
              </div>
            </div>
            <p className="text-light-40 mb-4 text-sm sm:text-base line-clamp-2">
              {item?.review || 'No review provided'}
            </p>
            {item?.status === 'pending' &&
              canEdit(getPermissionName('update:review')) && (
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => handleStatusChange(item?.id, 'rejected')}
                    className="px-4 py-2 sm:py-3 border flex items-center justify-center text-center border-primary text-primary text-sm font-medium bg-white hover:bg-primary-light transition-colors w-full rounded-2xl"
                  >
                    Reject
                  </button>

                  <button
                    type="button"
                    onClick={() => handleStatusChange(item?.id, 'approved')}
                    className="px-4 py-2 sm:py-3 border flex items-center justify-center text-center border-primary text-white text-sm font-medium bg-primary hover:bg-primary-dark transition-colors w-full rounded-2xl"
                  >
                    Approve
                  </button>
                </div>
              )}
          </div>
        ))}
      </div>

      <div className="flex justify-center mt-4">
        {states.pageInfo.page < states.pageInfo.totalPages && (
          <button
            className="btn-secondary"
            onClick={() => setParams({ page: page + 1 })}
          >
            Load more
          </button>
        )}
      </div>

      {states.error && (
        <div className="text-center text-red-500 mt-6">
          Error: {states.error}
        </div>
      )}

      {!states.isLoading && states.ratings.length === 0 && (
        <div className="text-center text-gray-400 mt-6">
          No ratings available.
        </div>
      )}
    </Layout>
  );
};

export default ReviewsDetails;
